import { useEffect, useId, useState } from 'react';

import { Button, Icon, IconButton } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select, Textarea } from '@ds/forms';
import { diaIso, moeda } from '@/comuns/formato';
import { quantidade, unidadeLabel } from '@/comuns/unidades';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { Fornecedor, PedidoDeCompra, Produto } from '@/data/tipos';
import { centavos } from '@/screens/vendas/NovoOrcamentoModal';

/** Item pré-carregado pela sugestão de compra. */
export interface ItemSugeridoParaPedido {
    produtoId: string;
    quantidade: number;
    custoUnitarioCentavos: number;
}

export interface PedidoModalProps {
    /** `null` fecha; um pedido edita; `'novo'` abre em branco. */
    alvo: PedidoDeCompra | 'novo' | null;
    /** Fornecedor e itens já escolhidos pela sugestão, ao abrir em branco. */
    partida?: { fornecedorId: string; itens: ItemSugeridoParaPedido[] } | null;
    fornecedores: Fornecedor[];
    produtos: Produto[];
    onClose: () => void;
    onSalvo: () => void;
}

interface Linha {
    produtoId: string;
    quantidade: string;
    custo: string;
}

const emReais = (centavosDoCusto: number) => (centavosDoCusto / 100).toFixed(2).replace('.', ',');

export function PedidoModal({ alvo, partida, fornecedores, produtos, onClose, onSalvo }: PedidoModalProps) {
    const aviso = useAviso();
    const idFornecedor = useId();
    const idPrevisao = useId();
    const idObservacoes = useId();

    const [fornecedorId, setFornecedorId] = useState('');
    const [previsao, setPrevisao] = useState('');
    const [observacoes, setObservacoes] = useState('');
    const [linhas, setLinhas] = useState<Linha[]>([]);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);
    /* Retrato do formulário ao abrir. "Tem linha" não é "mexeu": o pedido que
       vem da sugestão já nasce com itens, e pedia confirmação para fechar sem
       nenhuma alteração. */
    const [inicial, setInicial] = useState('');

    const novo = alvo === 'novo';

    useEffect(() => {
        if (!alvo) return;
        setErro(null);
        const partidaDoForm = alvo === 'novo'
            ? {
                fornecedorId: partida?.fornecedorId ?? fornecedores.find((f) => f.ativo)?.id ?? '',
                previsao: '',
                observacoes: '',
                linhas: (partida?.itens ?? []).map((i) => ({
                    produtoId: i.produtoId,
                    quantidade: String(i.quantidade),
                    custo: emReais(i.custoUnitarioCentavos),
                })),
            }
            : {
                fornecedorId: alvo.fornecedorId ?? '',
                previsao: alvo.previsaoEntrega ?? '',
                observacoes: alvo.observacoes ?? '',
                linhas: alvo.itens.map((i) => ({
                    produtoId: i.produtoId,
                    quantidade: String(i.quantidade),
                    custo: emReais(i.custoUnitarioCentavos),
                })),
            };
        setFornecedorId(partidaDoForm.fornecedorId);
        setPrevisao(partidaDoForm.previsao);
        setObservacoes(partidaDoForm.observacoes);
        setLinhas(partidaDoForm.linhas);
        setInicial(JSON.stringify(partidaDoForm));
    }, [alvo, partida, fornecedores]);

    if (!alvo) return null;

    const sujo = JSON.stringify({ fornecedorId, previsao, observacoes, linhas }) !== inicial;

    const produtoDe = (id: string) => produtos.find((p) => p.id === id);
    const subtotal = (linha: Linha) => (Number(linha.quantidade.replace(',', '.')) || 0) * centavos(linha.custo);
    const total = linhas.reduce((s, linha) => s + subtotal(linha), 0);

    /* Trocar o produto traz o custo do cadastro: é o preço que a clínica tem
       registrado, e digitar de novo é onde nasce o erro de centavo. */
    const trocarProduto = (indice: number, produtoId: string) => {
        setLinhas((atual) => atual.map((linha, i) => (i === indice
            ? { ...linha, produtoId, custo: emReais(produtoDe(produtoId)?.custoCentavos ?? 0) }
            : linha)));
    };

    const salvar = async (enviar: boolean): Promise<boolean> => {
        setSalvando(true);
        try {
            const pedido = await dados().salvarPedido({
                id: novo ? undefined : alvo.id,
                fornecedorId,
                previsaoEntrega: previsao || undefined,
                observacoes: observacoes || undefined,
                enviar,
                itens: linhas.filter((linha) => linha.produtoId).map((linha) => ({
                    produtoId: linha.produtoId,
                    quantidade: Number(linha.quantidade.replace(',', '.')) || 0,
                    custoUnitarioCentavos: centavos(linha.custo),
                })),
            });
            /* O VecchIA não manda nada ao fornecedor: "enviado" é o registro de
               que a clínica já fez o pedido por fora. O aviso diz exatamente isso. */
            aviso({
                title: enviar ? 'Pedido marcado como enviado' : 'Rascunho salvo',
                description: `${pedido.numero} · ${pedido.fornecedor} · ${moeda(pedido.totalCentavos)}`,
            });
            onSalvo();
            onClose();
            return true;
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar.');
            return false;
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open onClose={onClose} width={720}
            title={novo ? 'Novo pedido de compra' : `Editar pedido ${alvo.numero}`}
            description="O que chegar deste pedido entra no estoque como movimentação de entrada."
            dirty={sujo}
            /* Salvar ao sair guarda RASCUNHO: quem só queria fechar a janela não
               pode acabar registrando o pedido como feito. */
            onSave={() => salvar(false)} saveLabel="Salvar rascunho e fechar"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    {/* Rascunho é o pedido que ainda se discute com o fornecedor;
                        enviado é o que já foi pedido e a clínica espera receber. */}
                    <Button variant="ghost" onClick={() => void salvar(false)} disabled={salvando || linhas.length === 0}>
                        Salvar rascunho
                    </Button>
                    <Button onClick={() => void salvar(true)} loading={salvando} disabled={linhas.length === 0}>
                        Marcar como enviado
                    </Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.6fr) minmax(0,1fr)', gap: 14 }}>
                    <FormField label="Fornecedor" required htmlFor={idFornecedor}>
                        <Select
                            id={idFornecedor} value={fornecedorId} onChange={setFornecedorId}
                            options={fornecedores.map((f) => ({ value: f.id, label: f.ativo ? f.nome : `${f.nome} (inativo)` }))}
                        />
                    </FormField>
                    <FormField label="Previsão de entrega" htmlFor={idPrevisao} hint={fornecedores.find((f) => f.id === fornecedorId)?.prazoEntrega}>
                        <Input id={idPrevisao} type="date" value={previsao} onChange={setPrevisao} min={diaIso()} />
                    </FormField>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {linhas.map((linha, indice) => {
                        const produto = produtoDe(linha.produtoId);
                        const qtd = Number(linha.quantidade.replace(',', '.')) || 0;
                        return (
                            <div key={indice} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                    {/* A opção mostra o saldo: pedir sem ver o que ainda
                                        tem é como comprar duas vezes o mesmo frasco. */}
                                    <Select
                                        value={linha.produtoId} onChange={(v) => trocarProduto(indice, v)}
                                        aria-label={`Produto ${indice + 1}`}
                                        options={produtos.map((p) => ({
                                            value: p.id,
                                            label: `${p.nome} — ${quantidade(p.disponivel)} ${unidadeLabel(p.disponivel, p.unidade)} · mínimo ${quantidade(p.minimo)}`,
                                        }))}
                                    />
                                </span>
                                <span style={{ width: 92, flex: '0 0 auto' }}>
                                    <Input
                                        value={linha.quantidade} aria-label={`Quantidade do produto ${indice + 1}`}
                                        onChange={(v) => setLinhas((atual) => atual.map((x, i) => (i === indice ? { ...x, quantidade: v } : x)))}
                                    />
                                </span>
                                <span style={{ width: 108, flex: '0 0 auto', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                    {produto ? unidadeLabel(qtd, produto.unidade) : '—'}
                                </span>
                                <span style={{ width: 116, flex: '0 0 auto' }}>
                                    <Input
                                        value={linha.custo} aria-label={`Custo unitário do produto ${indice + 1}`}
                                        onChange={(v) => setLinhas((atual) => atual.map((x, i) => (i === indice ? { ...x, custo: v } : x)))}
                                    />
                                </span>
                                <span style={{ width: 110, flex: '0 0 auto', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, fontSize: 13 }}>
                                    {moeda(subtotal(linha))}
                                </span>
                                <IconButton
                                    size="sm" icon={<Icon name="trash-2" size={14} />} label={`Remover produto ${indice + 1}`}
                                    onClick={() => setLinhas((atual) => atual.filter((_, i) => i !== indice))}
                                />
                            </div>
                        );
                    })}
                    <Button
                        variant="ghost" size="sm" iconLeft={<Icon name="plus" size={14} />} style={{ alignSelf: 'flex-start' }}
                        disabled={produtos.length === 0}
                        onClick={() => setLinhas((atual) => [...atual, {
                            produtoId: produtos[0]?.id ?? '',
                            quantidade: '1',
                            custo: emReais(produtos[0]?.custoCentavos ?? 0),
                        }])}
                    >
                        Adicionar produto
                    </Button>
                </div>

                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
                    <span style={{ fontFamily: 'var(--font-ui)', fontSize: 14, fontWeight: 600 }}>Total do pedido</span>
                    <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-ui)', fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        {moeda(total)}
                    </span>
                </div>

                <FormField label="Observações" htmlFor={idObservacoes} hint="Nota fiscal, condição de pagamento, o que combinaram">
                    <Textarea id={idObservacoes} rows={2} value={observacoes} onChange={setObservacoes} />
                </FormField>
            </div>
        </Modal>
    );
}
